import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  useApiAdminsegmentsIdDelete,
  getApiAdminsegmentsGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type { SegmentSegmentCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type Segment = SegmentSegmentCollectionReadModel;

/**
 * Gère la suppression d'un segment : affiche une confirmation, supprime le segment
 * puis invalide la liste des segments.
 */
export function useDeleteSegment(onDeleted?: () => void) {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();

  const deleteMutation = useApiAdminsegmentsIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.segments.delete.success'));
        queryClient.invalidateQueries({ queryKey: getApiAdminsegmentsGetCollectionQueryKey() });
        onDeleted?.();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'delete', '/admin/segments')) {
          showOrvalErrorToast(error, t('admin.segments.delete.error'));
        } else {
          toast.error(t('admin.segments.delete.error'));
        }
      }
    }
  });

  const handleDelete = (segment: Segment) => {
    toast(t('admin.segments.delete.confirm', { name: segment.name }), {
      action: {
        label: t('label.confirm'),
        onClick: () => deleteMutation.mutate({ id: String(segment.id) })
      },
      cancel: {
        label: t('label.cancel'),
        onClick: () => {}
      }
    });
  };

  return { handleDelete };
}
